import { renderToStaticMarkup } from 'react-dom/server'
import { Droplet, Timer, TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'

/** The kinds of pin the irrigation map draws. */
export type IrrigationMarkerTone =
  | 'waterSource'
  | 'timer'
  | 'equipment'
  | 'valveBox'
  | 'problem'

export interface IrrigationMarkerProps {
  tone: IrrigationMarkerTone
  /** The text inside an equipment pin: a valve's zone number, or a kind's initials. */
  glyph?: string
  selected: boolean
  /** A change to it is still waiting to reach the server. */
  pending: boolean
  /** It has an open problem recorded on it as a whole. */
  hasOpenProblem: boolean
  /** Last visit's, shown for reference: there to be checked, not this visit's. */
  faint?: boolean
}

/** Pin diameter in CSS pixels; Leaflet anchors the icon on its center. */
export const MARKER_SIZE_PX = 26

/*
 * Every class is written out here, because Tailwind cannot generate one
 * assembled at runtime (see "Species are configured, their marks are not" in
 * docs/field-survey.md). The fills are the saturated map palette: a pale wash
 * disappears over turf.
 */
const TONE_CLASS: Record<IrrigationMarkerTone, string> = {
  waterSource: 'bg-map-blue',
  timer: 'bg-map-slate',
  equipment: 'bg-map-teal',
  valveBox: 'bg-map-teal',
  problem: 'bg-map-crimson',
}

/**
 * A box's zones can run to five characters ("17·23"), which only fit the pin
 * a size or two smaller.
 */
function glyphSize(glyph: string | undefined): string {
  const length = glyph?.length ?? 0
  if (length >= 4) return 'text-[8px] tracking-tight'
  if (length === 3) return 'text-[10px]'
  return 'text-[11px]'
}

/**
 * One irrigation pin: a filled disc with a white halo, so it reads over
 * aerial imagery, and either an icon or a short glyph.
 *
 * Kind is carried by both color and content (an icon or initials), for the
 * reason tree marks carry species twice: hue alone fails a color-blind reader
 * and fails again in grayscale. A valve shows its zone number, because "which
 * zone is this box" is the question a technician is standing over it asking.
 * A valve box is the same color as other equipment but square, like the box
 * itself, and shows the zones of the valves in it.
 */
export function IrrigationMarker({
  tone,
  glyph,
  selected,
  pending,
  hasOpenProblem,
  faint = false,
}: IrrigationMarkerProps) {
  return (
    <div
      className={cn('relative', faint ? 'opacity-50' : null)}
      style={{ width: MARKER_SIZE_PX, height: MARKER_SIZE_PX }}
    >
      <div
        className={cn(
          'flex h-full w-full items-center justify-center border-2 border-map-halo font-semibold leading-none text-map-halo shadow-sm',
          tone === 'valveBox' ? 'rounded-[5px]' : 'rounded-full',
          glyphSize(glyph),
          TONE_CLASS[tone],
          // Unsynced: the halo goes dashed, the same signal the tree pins use
          // for "only on this phone so far".
          pending ? 'border-dashed' : null,
          selected ? 'ring-2 ring-brand ring-offset-1' : null,
        )}
      >
        {tone === 'waterSource' ? (
          <Droplet className="h-3.5 w-3.5" aria-hidden="true" />
        ) : null}
        {tone === 'timer' ? (
          <Timer className="h-3.5 w-3.5" aria-hidden="true" />
        ) : null}
        {tone === 'problem' ? (
          <TriangleAlert className="h-3.5 w-3.5" aria-hidden="true" />
        ) : null}
        {tone === 'equipment' || tone === 'valveBox' ? (
          <span>{glyph ?? ''}</span>
        ) : null}
      </div>
      {hasOpenProblem && tone !== 'problem' ? (
        <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border border-map-halo bg-map-crimson" />
      ) : null}
    </div>
  )
}

export function irrigationMarkerHtml(props: IrrigationMarkerProps): string {
  return renderToStaticMarkup(<IrrigationMarker {...props} />)
}

/**
 * Everything that changes how a pin looks, as one comparable string, so the
 * map redraws only the pin that changed. It covers exactly the props the
 * marker reads.
 */
export function irrigationMarkerSignature(
  props: IrrigationMarkerProps,
): string {
  return [
    props.tone,
    props.glyph ?? '',
    props.selected ? 's' : '',
    props.pending ? 'p' : '',
    props.hasOpenProblem ? '!' : '',
    props.faint === true ? 'f' : '',
  ].join('|')
}
